import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CodeIcon, MicIcon, WrenchIcon, BookIcon, TrophyIcon, ImageIcon, TargetIcon, CalendarIcon, MoneyIcon, ShareIcon, CloseIcon, WhatsAppIcon, InstagramIcon, TwitterIcon, FacebookIcon, LinkedInIcon, LinkIcon } from './Icons';
import '../styles/EventCard.css';

interface Event {
  id: string;
  name: string;
  description: string;
  type: 'event' | 'workshop' | 'exhibition' | 'seminar' | 'competition' | 'hackathon' | 'conference' | 'other';
  image?: string;
  date: string;
  created_at: string;
  club_id?: string;
  club_name?: string;
  fee: number;
}

interface EventCardProps {
  event: Event;
  onEnroll?: (eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onEnroll }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const getEventTypeIcon = () => {
    const iconMap: Record<string, React.ReactNode> = {
      hackathon: <CodeIcon size={16} color="#00ffff" />,
      conference: <MicIcon size={16} color="#00ffff" />,
      workshop: <WrenchIcon size={16} color="#00ffff" />,
      seminar: <BookIcon size={16} color="#00ffff" />,
      competition: <TrophyIcon size={16} color="#00ffff" />,
      exhibition: <ImageIcon size={16} color="#00ffff" />,
    };
    return iconMap[event.type] || <TargetIcon size={16} color="#00ffff" />;
  };

  const getEventTypeLabel = () => {
    return (event.type || 'event').charAt(0).toUpperCase() + (event.type || 'event').slice(1);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleCardClick = () => navigate(`/public/events/${event.id}`);

  const shareUrl = `${window.location.origin}/public/events/${event.id}`;
  const shareText = `Check out this event: ${event.name}`;

  const handleShare = useCallback((e: React.MouseEvent, platform: string) => {
    e.stopPropagation();
    const actions: Record<string, () => void> = {
      whatsapp: () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank'),
      instagram: () => { navigator.clipboard.writeText(shareUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); },
      twitter: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank'),
      facebook: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank'),
      linkedin: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank'),
      copy: () => { navigator.clipboard.writeText(shareUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); },
    };
    actions[platform]?.();
    if (platform !== 'instagram' && platform !== 'copy') setShareSheetOpen(false);
  }, [shareUrl, shareText]);

  // Close share sheet on Escape
  useEffect(() => {
    if (!shareSheetOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setShareSheetOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shareSheetOpen]);

  const platforms = [
    { id: 'whatsapp',  icon: <WhatsAppIcon size={18} color="#fff" />, label: 'WhatsApp', color: '#25D366' },
    { id: 'instagram', icon: <InstagramIcon size={18} color="#fff" />, label: 'Instagram', color: '#E1306C' },
    { id: 'twitter',   icon: <TwitterIcon size={18} color="#fff" />,  label: 'Twitter',   color: '#1DA1F2' },
    { id: 'facebook',  icon: <FacebookIcon size={18} color="#fff" />, label: 'Facebook',  color: '#4267B2' },
    { id: 'linkedin',  icon: <LinkedInIcon size={18} color="#fff" />, label: 'LinkedIn',  color: '#0A66C2' },
  ];

  return (
    <>
      <motion.div
        className="ec-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => { setIsHovered(false); }}
        whileHover={{ y: -6 }}
        transition={{ duration: 0.3 }}
      >
        {/* Image */}
        <div className="ec-image" onClick={handleCardClick}>
          {event.image ? (
            <img src={event.image} alt={event.name} />
          ) : (
            <div className="ec-placeholder">
              <span className="ec-placeholder-icon">{getEventTypeIcon()}</span>
            </div>
          )}
          <span className="ec-badge">
            <span>{getEventTypeIcon()}</span>
            <span>{getEventTypeLabel()}</span>
          </span>

          {/* Neon glow line at bottom of image */}
          <div className="ec-image-glow" />
        </div>

        {/* Content */}
        <div className="ec-content">
          <h3 className="ec-title" onClick={handleCardClick}>{event.name}</h3>

          <div className="ec-meta">
            <span className="ec-meta-item"><CalendarIcon size={13} color="#00ffff" style={{marginRight: 4, verticalAlign: 'middle'}} /> {formatDate(event.date)}</span>
            <span className="ec-meta-item"><MoneyIcon size={13} color="#00ffff" style={{marginRight: 4, verticalAlign: 'middle'}} /> {event.fee ? `₹${event.fee}` : 'Free'}</span>
            <span className="ec-meta-item">Club: {event.club_name || 'College Event'}</span>
          </div>

          <div className="ec-actions">
            <motion.button
              className="ec-view-btn"
              onClick={handleCardClick}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Explore
            </motion.button>
            <motion.button
              className="ec-share-trigger"
              onClick={(e) => { e.stopPropagation(); setShareSheetOpen(true); }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Share"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Hover glow border */}
        <div className="ec-glow-border" />
      </motion.div>

      {/* ===== Professional Share Sheet (Instagram/WhatsApp style) ===== */}
      <AnimatePresence>
        {shareSheetOpen && (
          <motion.div
            className="share-sheet-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShareSheetOpen(false)}
          >
            <motion.div
              className="share-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle bar */}
              <div className="share-sheet-handle" />

              {/* Header */}
              <div className="share-sheet-header">
                <h3>Share Event</h3>
                <button className="share-sheet-close" onClick={() => setShareSheetOpen(false)}><CloseIcon size={14} /></button>
              </div>

              {/* Event preview */}
              <div className="share-sheet-preview">
                <div className="share-sheet-preview-icon">{getEventTypeIcon()}</div>
                <div className="share-sheet-preview-info">
                  <p className="share-sheet-preview-title">{event.name}</p>
                  <p className="share-sheet-preview-meta">{formatDate(event.date)} · {event.fee ? `₹${event.fee}` : 'Free'}</p>
                </div>
              </div>

              {/* Platform icons row */}
              <div className="share-sheet-platforms">
                {platforms.map((p) => (
                  <motion.button
                    key={p.id}
                    className="share-platform"
                    onClick={(e) => handleShare(e, p.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <div className="share-platform-circle" style={{ background: p.color }}>
                      <span>{p.icon}</span>
                    </div>
                    <span className="share-platform-label">{p.label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Copy link bar */}
              <div className="share-sheet-link">
                <div className="share-sheet-link-url">
                  <span>{shareUrl.replace(/^https?:\/\//, '').slice(0, 40)}...</span>
                </div>
                <motion.button
                  className={`share-sheet-copy-btn ${linkCopied ? 'copied' : ''}`}
                  onClick={(e) => handleShare(e, 'copy')}
                  whileTap={{ scale: 0.95 }}
                >
                  {linkCopied ? '✓ Copied' : 'Copy Link'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EventCard;
